import { html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { HelpLightElement } from "./help-light-element.js";
import { readJsonScript } from "../config.js";
import { mirrorHelpThemeToAfasTheme } from "../theme-bridge.js";
import {
  IframeHeightReporter,
  parseIntegrationInitialState,
  type IntegrationCategory,
} from "../integration.js";

/**
 * Light-DOM widget for the InSite "Integratie" iframe. Renders the eight fixed
 * category columns from the server-rendered `#integration-initial-state` island
 * (data stays server-computed; only presentation moved to the frontend). Mirrors
 * the Help `data-theme` onto the AFAS `theme` attribute so the `--color-*` tokens
 * resolve, and reports its content height to the embedding host. Light DOM is used
 * so the page-level theme tokens and the host height measurement see real layout,
 * consistent with `help-jonas-cms-login`.
 */
@customElement("help-jonas-integration")
export class HelpJonasIntegration extends HelpLightElement {
  @property({ attribute: "config-id" }) accessor configId =
    "integration-initial-state";

  @state() private accessor categories: IntegrationCategory[] = [];
  @state() private accessor emptyText = "";

  private themeObserver: MutationObserver | null = null;
  private readonly heightReporter = new IframeHeightReporter();

  override connectedCallback(): void {
    super.connectedCallback();
    this.readInitialState();
    this.themeObserver = mirrorHelpThemeToAfasTheme(document.documentElement);
    this.heightReporter.start();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.themeObserver?.disconnect();
    this.themeObserver = null;
    this.heightReporter.stop();
  }

  private readInitialState(): void {
    try {
      const state = parseIntegrationInitialState(
        readJsonScript(document, this.configId),
      );
      this.emptyText = state.emptyText;
      this.categories = state.categories;
    } catch {
      // No/invalid island: render an empty grid rather than throw.
      this.categories = [];
    }
  }

  override render(): TemplateResult {
    return html`
      <style>
        help-jonas-integration {
          display: block;
          width: 90%;
          max-width: 1200px;
          margin: 0 auto;
          padding: 24px 0 32px;
          font-family: var(--font-family, "Inter", system-ui, sans-serif);
          color: var(--color-text-on-background-800, #41474d);
        }
        html[data-theme="dark"] body {
          background: var(--color-background-900, #1e293b);
          color: var(--color-text-on-background-800, #cbd5e1);
        }
        help-jonas-integration .integration-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
          gap: 24px 32px;
        }
        help-jonas-integration .integration-card {
          min-width: 0;
        }
        help-jonas-integration .integration-card__head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding-bottom: 8px;
          margin-bottom: 12px;
          border-bottom: 1px solid var(--color-foreground-300, #c3cbd3);
        }
        help-jonas-integration .integration-card__title {
          margin: 0;
          font-size: 20px;
          line-height: 1.3;
          font-weight: 600;
          color: var(--color-primary-700, #005faa);
        }
        help-jonas-integration .integration-card__icon {
          flex-shrink: 0;
          width: 40px;
          height: 40px;
          object-fit: contain;
        }
        help-jonas-integration .integration-card__list {
          margin: 0;
          padding: 0;
          list-style: none;
        }
        help-jonas-integration .integration-card__list li {
          padding-left: 30px;
          min-height: 30px;
          line-height: 1.6;
          margin-bottom: 4px;
          background: url("/images/integration/1-1.png") left 6px no-repeat;
        }
        help-jonas-integration .integration-card__list a {
          color: var(--color-primary-600, #0078d7);
          font-weight: 600;
          text-decoration: none;
        }
        help-jonas-integration .integration-card__list a:hover,
        help-jonas-integration .integration-card__list a:focus {
          text-decoration: underline;
        }
        help-jonas-integration .integration-card__empty {
          margin: 0;
          color: var(--color-foreground-500, #6b7280);
        }
        @media (max-width: 920px) {
          help-jonas-integration {
            width: 97%;
          }
        }
      </style>
      <div class="integration-grid">
        ${this.categories.map((category) => this.renderCategory(category))}
      </div>
    `;
  }

  private renderCategory(category: IntegrationCategory): TemplateResult {
    return html`
      <section class="integration-card" data-category=${category.key}>
        <header class="integration-card__head">
          <h2 class="integration-card__title">${category.label}</h2>
          ${category.iconUrl
            ? html`<img
                class="integration-card__icon"
                src=${category.iconUrl}
                alt=""
                aria-hidden="true"
              />`
            : nothing}
        </header>
        ${category.items.length > 0
          ? html`<ol class="integration-card__list">
              ${category.items.map(
                (item) =>
                  html`<li>
                    <a
                      href=${item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      >${item.title}</a
                    >
                  </li>`,
              )}
            </ol>`
          : html`<p class="integration-card__empty">
              <em>${this.emptyText}</em>
            </p>`}
      </section>
    `;
  }
}
